// منطقِ خالصِ «سفرِ کاربر»: نامِ صفحه، برچسبِ دکمه، شروع و پایانِ نشست.
//
// از `journey.ts` جدا است به همان دلیلی که `analyticsQueue` از `analytics`
// جدا است: این‌جا هیچ تایمری، شبکه‌ای و حالتِ سراسری‌ای نیست، پس با
// `npm test` بدونِ دیتابیس و مرورگر اجرا می‌شود.

/* ─────────────────────────── نامِ صفحه ─────────────────────────── */

/**
 * نامِ یکسانِ صفحه برای هر دو اپ.
 *
 * ═══ چرا لازم شد ═══
 *
 * وب `/signal/:id` می‌فرستاد و موبایل `/(tabs)/discover` و `/signal/[id]`.
 * دومی‌ها **اصلاً ثبت نمی‌شدند**: `sanitizeProps` فقط
 * `[A-Za-z0-9_.:/-]` را می‌پذیرد و پرانتز و کروشه در آن نیست، پس کلید
 * بی‌صدا می‌افتاد. نتیجه‌اش در دیتابیسِ ابری ۱٬۲۳۱ ردیفِ `screen_view` با
 * `props` **خالی** بود — یعنی تمامِ صفحه‌های تب‌دار و تمامِ مسیرهای پویای
 * موبایل، از روزِ اول، بی‌نام.
 *
 * پس نام این‌جا نرمال می‌شود و نه در هر اپ:
 *
 *   `/(tabs)/discover`   →  `/discover`     گروهِ مسیرِ Expo Router
 *   `/signal/[id]`       →  `/signal/:id`   پارامتر، به شکلِ وب
 *   `/event/9f3c…`       →  `/event/:id`    شناسه‌ی واقعی (کارِ خودِ اپ‌ها)
 *
 * ⚠ نام هیچ‌وقت محتوا نیست. شناسه‌ی سیگنالِ کسی نباید ثبت شود — همان قولِ
 * سرِ `lib/analytics` — پس هر بخشی که شبیهِ شناسه باشد به `:id` تبدیل
 * می‌شود، حتی اگر اپ خودش یادش رفته باشد.
 */
export function normalizeScreen(raw: string): string {
  if (!raw) return '/';
  let s = raw.split('?')[0].split('#')[0];
  // گروه‌های Expo Router: `(tabs)`، `(auth)` — پوشه‌اند، نه صفحه.
  s = s.replace(/\/\([^)]*\)/g, '');
  // `[id]` و `[...rest]` → `:id`
  s = s.replace(/\[\.\.\.([^\]]+)\]/g, ':$1').replace(/\[([^\]]+)\]/g, ':$1');
  // هر بخشی که خودش داده است و نه مسیر: uuid، عدد، توکنِ بلند.
  s = s.split('/').map((seg) => (isDataSegment(seg) ? ':id' : seg)).join('/');
  s = s.replace(/\/{2,}/g, '/');
  if (s.length > 1 && s.endsWith('/')) s = s.slice(0, -1);
  return s || '/';
}

const UUID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

const isDataSegment = (seg: string): boolean => {
  if (!seg || seg.startsWith(':')) return false;
  if (UUID.test(seg)) return true;
  if (/^\d+$/.test(seg)) return true;
  // توکنِ دعوت و لینکِ عمومی: بلند و بی‌معنی.
  return seg.length >= 16 && /^[A-Za-z0-9_-]+$/.test(seg);
};

/* ─────────────────────────── برچسبِ دکمه ─────────────────────────── */

/**
 * برچسبی که کیت‌ها می‌دهند (`chip:دعوت از دوستان`) را به چیزی تبدیل می‌کند
 * که می‌شود ذخیره‌اش کرد (`chip:inviteFriends`).
 *
 * ورودی‌ها همیشه یکی از دو شکل‌اند: `نوع:متن` یا فقط `متن`. متن از
 * دیکشنریِ ترجمه می‌آید، پس `keyOf` معمولاً کلیدش را پیدا می‌کند. هر چیزی
 * که پیدا نشود — اسمِ آدم، تگ، شهر، متنی که با `{param}` ساخته شده —
 * `other` می‌شود.
 *
 * ⚠ «پیدا نشد» عمداً به `other` می‌رسد و نه به «هیچی»: تعدادِ لمس‌های
 * ناشناخته‌ی یک صفحه خودش خبر است — یعنی جایی هست که برچسبش قابلِ شمردن
 * نیست. اگر بی‌صدا می‌افتاد، آن صفحه بی‌دکمه به نظر می‌رسید.
 *
 * ⚠ `allowRaw` یعنی «این رشته از کدِ خودمان آمده، نه از صفحه».
 *
 * برچسبِ کیت یک ثابتِ داخلِ کد است (`card`, `tab_discover`) و نگه داشتنش
 * امن است. ولی برچسبی که از **متنِ عنصرِ DOM** درآمده هرچه باشد می‌تواند
 * باشد — و یک بار همین اتفاق افتاد: چیپِ دسته‌بندیِ صفحه‌ی کشف متنش
 * `Music241` بود (نام + شمارنده، بی‌فاصله)، از الگوی «برچسبِ برنامه‌نویسی»
 * رد می‌شد و عیناً ذخیره شد. همان مسیر، برای چیپی که اسمِ یک **آدم** روی
 * آن است، اسمِ آن آدم را ذخیره می‌کرد.
 *
 * پس مسیرِ DOM با `allowRaw = false` صدا زده می‌شود: یا کلیدِ ترجمه، یا
 * `other`. استثنا فقط `data-tap` است، که خودمان روی عنصر نوشته‌ایم.
 */
export function analyticsLabel(
  raw: string, keyOf: (text: string) => string | null, allowRaw = true,
): string {
  const s = (raw ?? '').trim();
  if (!s) return 'other';
  const i = s.indexOf(':');
  const kind = i > 0 ? s.slice(0, i) : '';
  const text = i > 0 ? s.slice(i + 1) : s;
  const key = text ? keyOf(text) : null;
  const tail = key ?? (allowRaw && isSafeLabel(text) ? text : 'other');
  return clip(kind ? `${kind}:${tail}` : tail);
};

/**
 * برچسبی که خودش از قبل شناسه‌ی برنامه‌نویسی است (`open`, `save`,
 * `tab_discover`) و اصلاً از دیکشنری نیامده. این‌ها را نگه می‌داریم؛ چیزی
 * که آدم نوشته باشد این الگو را ندارد.
 */
const isSafeLabel = (s: string): boolean => /^[a-z][a-z0-9_.-]{0,29}$/i.test(s);

const clip = (s: string): string => (s.length <= 40 ? s : s.slice(0, 40));

/* ─────────────────────────── نشست ─────────────────────────── */

/**
 * بعد از این‌قدر بی‌حرکتی، برگشتن به اپ یک نشستِ **تازه** است.
 *
 * سی دقیقه قراردادِ رایجِ همه‌ی ابزارهای تحلیلی است و دلیلش هم رفتاری است:
 * کسی که گوشی را زمین گذاشته و نیم‌ساعت بعد برداشته، کارِ قبلی‌اش را ادامه
 * نمی‌دهد. بدونِ این قاعده، «مدتِ نشست» روی موبایل عملاً «چند ساعت اپ در
 * جیب باز بوده» را می‌شمرد.
 */
export const SESSION_IDLE_MS = 30 * 60 * 1000;

export const isNewSession = (lastActivityMs: number | null, nowMs: number): boolean =>
  lastActivityMs === null || nowMs - lastActivityMs >= SESSION_IDLE_MS;

/**
 * آیا برای این نشست `session_end` فرستاده شود؟ (WHOSUP-295)
 *
 * قاعده: هر شناسه‌ی نشست **حداکثر یک** پایان دارد. پنهان شدنِ تب یا رفتنِ
 * اپ به پس‌زمینه پایان را می‌فرستد؛ برگشتِ کوتاه (کمتر از `SESSION_IDLE_MS`)
 * همان نشست را ادامه می‌دهد و پنهان شدن‌های بعدی‌اش **پایانِ دوم** نمی‌فرستند.
 * برگشت بعد از بی‌کاریِ طولانی شناسه را عوض می‌کند (`isNewSession`)، با
 * `session_start` تازه، و آن نشستِ تازه دوباره حقِ یک پایان دارد.
 *
 * پیش از این هر پنهان شدن یک پایان می‌فرستاد، و روی موبایل هر رفتن به
 * پس‌زمینه دو تا (`inactive` و بعد `background`) — پایان‌ها چند برابرِ شروع‌ها.
 */
export const shouldSendSessionEnd = (
  endedSessionId: string | null, currentSessionId: string,
): boolean => endedSessionId !== currentSessionId;

/**
 * حالتِ نشست — همان سه چیزی که `analytics.ts` برای شروع و پایان نگه می‌دارد.
 * (WHOSUP-317)
 *
 * دو تابعِ زیر **تنها** جایی‌اند که شروع و پایان تصمیم گرفته می‌شود، و
 * هر دو اپ از همین‌ها رد می‌شوند: وب از `visibilitychange` و موبایل از
 * `AppState`. خالص‌اند تا بشود رفت‌وبرگشتِ پس‌زمینه را با `npm test`
 * شبیه‌سازی کرد و دید که برای هر پایان یک شروع با همان شناسه هست.
 */
export interface SessionClock {
  id: string;
  /** آخرین رویدادِ ثبت‌شده؛ `null` یعنی هنوز هیچ (اجرای سرد). */
  lastActivity: number | null;
  /** نشستی که `session_end`ـش رفته — نگاه کن به `shouldSendSessionEnd`. */
  endedId: string | null;
}

/**
 * پیش از هر رویداد: اگر بی‌کاری طولانی بوده (یا این اولین رویداد است)،
 * شناسه‌ی تازه می‌گیرد و `true` برمی‌گرداند — یعنی صدازننده باید
 * `session_start` بفرستد.
 */
export function rotateSession(c: SessionClock, nowMs: number, newId: () => string): boolean {
  if (!isNewSession(c.lastActivity, nowMs)) return false;
  c.id = newId();
  c.lastActivity = nowMs;
  return true;
}

/**
 * رفتن به پس‌زمینه / پنهان شدنِ تب. رویدادهایی که باید ثبت شوند را به
 * ترتیب برمی‌گرداند. نشستِ بی‌رویداد پایانی ندارد؛ اگر بی‌کاری طولانی بوده،
 * اول نشستِ تازه شروع می‌شود و پایان روی همان شناسه‌ی **نهایی** می‌نشیند.
 */
export function sessionEndSignals(
  c: SessionClock, nowMs: number, newId: () => string,
): ('session_start' | 'session_end')[] {
  if (c.lastActivity === null) return [];
  const out: ('session_start' | 'session_end')[] = [];
  if (rotateSession(c, nowMs, newId)) out.push('session_start');
  if (shouldSendSessionEnd(c.endedId, c.id)) {
    c.endedId = c.id;
    out.push('session_end');
  }
  return out;
}

/* ─────────────────────────── ورودی ─────────────────────────── */

/** «از کجا آمد» — چهار حالت، و هیچ‌کدام متنِ آزاد نیست. */
export type EntryKind = 'direct' | 'referral' | 'invite' | 'share' | 'deeplink';

export interface Entry {
  kind: EntryKind;
  /** میزبانِ ارجاع‌دهنده یا نامِ کمپین. فقط وقتی معنی دارد که بیرونی باشد. */
  ref?: string;
}

/**
 * از آدرسِ ورود و ارجاع‌دهنده، «از کجا آمد» را می‌سازد.
 *
 * ⚠ فقط **میزبان** از ارجاع‌دهنده برداشته می‌شود، نه مسیرش: مسیرِ صفحه‌ی
 * ارجاع‌دهنده می‌تواند خودش داده باشد (جستجوی کسی، صفحه‌ی شخصیِ کسی)، و
 * چیزی که ما می‌خواهیم بدانیم «از کدام سایت» است.
 *
 * ⚠ ارجاعِ هم‌میزبان یعنی «مستقیم»: کاربری که داخلِ خودِ سایت جابه‌جا شده
 * تازه از بیرون نیامده. بدونِ این بند، هر نشستِ دوم «ارجاع از خودمان»
 * می‌شد.
 */
export function classifyEntry(url: string, referrer?: string | null): Entry {
  const path = normalizeScreen(pathOf(url));
  if (path.startsWith('/i/')) return { kind: 'invite' };
  if (path.startsWith('/s/')) return { kind: 'share' };

  const host = hostOf(referrer ?? '');
  const self = hostOf(url);
  if (host && host !== self) return { kind: 'referral', ref: host };

  const camp = paramOf(url, 'utm_source') ?? paramOf(url, 'ref');
  if (camp && isSafeLabel(camp)) return { kind: 'referral', ref: camp };

  // آدرسی که میزبانِ http ندارد یعنی لینکِ عمیقِ اپ (`outami://…`).
  if (url && !/^https?:/i.test(url) && /:\/\//.test(url)) return { kind: 'deeplink' };
  return { kind: 'direct' };
}

const pathOf = (url: string): string => {
  const m = /^[a-z][a-z0-9+.-]*:\/\/[^/]*(\/[^?#]*)?/i.exec(url ?? '');
  if (m) return m[1] ?? '/';
  return (url ?? '').split('?')[0].split('#')[0] || '/';
};

const hostOf = (url: string): string => {
  const m = /^[a-z][a-z0-9+.-]*:\/\/([^/?#]+)/i.exec(url ?? '');
  const host = m?.[1]?.toLowerCase().replace(/^www\./, '') ?? '';
  // پورت را می‌اندازیم: `localhost:5173` و `localhost` یک جا هستند.
  return host.split(':')[0];
};

const paramOf = (url: string, key: string): string | null => {
  const m = new RegExp(`[?&]${key}=([^&#]+)`).exec(url ?? '');
  return m ? decodeURIComponent(m[1]) : null;
};
